import * as React from 'react';
import { useState } from 'react';
import { graphql } from 'gatsby';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import PageWrapper from '../components/Common/PageWrapper/PageWrapper';
import Tab from '../components/Common/Tab/Tab';
import { CarouselItemI } from '../interfaces/CarouselItemI';
import scrollTo from 'gatsby-plugin-smoothscroll';
import PageTitleSection from '../components/Sections/Common/PageTitleSection/PageTitleSection';
import HeroSection from '../components/Sections/Services/HeroSection/HeroSection';
import IntroductionSection from '../components/Sections/Services/IntroductionSection/IntroductionSection';
import LocationsSection from '../components/Sections/Services/LocationsSection/LocationsSection';
import PricingSection from '../components/Sections/Services/PricingSection/PricingSection';
import SlideSection from '../components/Sections/Services/SlideSection/SlideSection';
import ServicesTabs from '../components/Sections/Services/ServicesTabs/ServicesTabs';
import { PagesList } from '../enums/PagesList';
import { Application } from '../interfaces/professional/Professional';
import { distanceConverter } from '../utils/distanceConverter';
import { TemplatesList } from '../enums/TemplatesList';
import {sectorTranslation, serviceTranslation} from '../utils/translationsUtils';

const Services = ({ data, location: windowLocation }: Application) => {
  const { t } = useTranslation();
  const { longName, location, sectors, customSectors, services, locations, mobility, businessInfo, template } = data.professional.data;
  const sectorsList = sectors
    .map(sectors => sectors.title)
    .concat(customSectors)
    .join(', ');

  const { language } = useI18next();

  const [manualActiveTab, setManualActiveTab] = useState<string>();

  const slierItems: Array<CarouselItemI> = services.map(item => ({
    id: item.id,
    title: item?.sector?.title || item.customSector,
    image: item.picture?.url,
    action: () => undefined,
  }));

  const setActiveTab = (id: string) => {
    setManualActiveTab(id);
    scrollTo('#servicesTab');
  };

  const tabContentMapper = services.map(it => serviceTranslation(it, language as "en" | "fr")).map(item => {
    const specialties = [...item.specialties.map(item => item.title), ...item.customSpecialties];
    const benefits = [...item.helps.map(item => item.title), ...item.customHelps];

    // if (item.sector?.benefits?.length) {
    //   benefits = [...benefits, ...item.sector.benefits];
    // }

    const translatedSector = sectorTranslation(sectors, language as "en" | "fr", item?.sector);

    return (
      <Tab label={translatedSector?.title || item.customSector} key={item.id} id={item.id}>
        <HeroSection image={item.picture?.url} template={template.template as TemplatesList} />
        <IntroductionSection
          specialties={specialties}
          serviceId={item.id}
          introText={item.experience.explanatory}
          benefits={benefits}
          targetAudience={item.targetAudience}
          template={template.template as TemplatesList}
          image={item.picture?.url}
          serviceName={translatedSector?.title || item.customSector}
        />
        <LocationsSection
          serviceId={item.id}
          serviceName={translatedSector?.title || item.customSector}
          primaryLocation={location}
          otherLocations={locations}
          phoneNumber={businessInfo.phone}
          mobilityRadius={mobility && distanceConverter(mobility.radius, mobility.unit === 'km')}
          appointmentTypes={item.appointmentTypes.concat(
            item.customAppointmentTypes.map((item, index) => ({ title: item, id: index.toString() })),
          )}
          template={template.template as TemplatesList}
        />
        <PricingSection
          serviceName={translatedSector?.title || item.customSector}
          rates={item.rates}
          paymentsMethods={businessInfo.paymentMethod}
          paymentsAdditionalDescription={item.ratesDescription}
          template={template.template as TemplatesList}
        />
      </Tab>
    );
  });

  //TODO meta integration with BE
  return (
    <PageWrapper
        windowLocation={windowLocation}
      template={template.template as TemplatesList}
      currentPage={t('header.menu.services.label')}
      color={template.color}
      pageId={PagesList.SERVICES}
      metaDescription={t('SEO.services.description', { longName, primaryLocation: location.placeDescription, sectorsList })}
      metaTitle={t('SEO.services.title', { longName })}
    >
      <PageTitleSection template={template.template as TemplatesList} smallPadding>
        {t('services.h1Title')}
      </PageTitleSection>
      <ServicesTabs manualActiveTabId={manualActiveTab} template={template.template as TemplatesList}>
        {tabContentMapper}
      </ServicesTabs>
      {slierItems.length > 1 && (
        <SlideSection sliderItems={slierItems} slideClickAction={setActiveTab} template={template.template as TemplatesList} />
      )}
    </PageWrapper>
  );
};

export default Services;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    professional {
      data {
        template {
          template
          color
        }
        shortName
        longName
        description
        mobility {
          unit
          radius
        }
        location {
          placeDescription
          placeId
          name
          longitude
          latitude
        }
        locations {
          placeDescription
          placeId
          name
          longitude
          latitude
        }
        businessInfo {
          email
          phone
          euVatNumber
          officialId
          paymentMethod
        }
        customSectors
        sectors {
          id
          title
          activityType
          pictures {
            id
            url
            group
            description
          }
          benefits {
            title
            translated {
                en
                fr
            }
          }
          malePractitioner
          femalePractitioner
          descriptionTitle
          descriptionSubTitle
          shortDescription
          longDescription
          specialties {
            id
            title
          }
          translated {
              en
              fr
          }
        }
        services {
          id
          isPrimary
          customSector
          sector {
            id
            title
            activityType
            pictures {
              id
              url
              group
              description
            }
            benefits {
              title
              translated {
                  en
                  fr
              }
            }
            malePractitioner
            femalePractitioner
            descriptionTitle
            descriptionSubTitle
            shortDescription
            longDescription
            translated {
              en
              fr
            }
            specialties {
              id
              title
              translated {
                en
                fr
              }
            }
          }
          specialties {
            id
            title
            translated {
                en
                fr
            }
          }
          customSpecialties
          customHelps
          customAppointmentTypes
          appointmentTypes {
            id
            title
          }
          experience {
            year
            explanatory
            hideOnSite
          }
          helps {
            title
            translated {
                en
                fr
              }
          }
          targetAudience
          rates {
            description
            price
            currency
            active
          }
          ratesDescription
          picture {
            id
            url
            group
            description
          }
        }
      }
    }
  }
`;
